import React, { useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { STATUS_COLORS } from "../../../../constants/statusColors";
import { EVWriteBackStatus } from "../../../../enums/evBvWriteBackStatus";
import { MedicalPaOrdersAuthStatus } from "../../../../enums/medicalPaOrdersAuthStatus";
import { ModalId } from "../../../../enums/modalId";
import { setOpenedModalId } from "../../../../redux/slice/modalSlice";
import { AppDispatch } from "../../../../redux/store/store";
import CallBackIconWithBgColor from "../../../../svg/callBackIconWithBgColor";
import ToolTipUsingPortal from "../../../toolTipUsingPortal/toolTipUsingPortal";

interface RpaStatusCellProps {
  value: {
    status: string;
    filePaths: any;
    shouldPropagate?: boolean;
    id?: string;
    masterAuthStatus?: string;
  };
  propagateEventOnClickInRpaStatusCell?: (id: string) => void;
}

export const RpaStatusCell: React.FC<RpaStatusCellProps> = ({
  value,
  propagateEventOnClickInRpaStatusCell,
}) => {
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
  const commentModalRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch<AppDispatch>();

  const getColorBasedOnStatus = () => {
    switch (value.status) {
      case EVWriteBackStatus.SUCCESS:
        return STATUS_COLORS.SUCCESS.stroke;
      case EVWriteBackStatus.ERROR:
        return STATUS_COLORS.ERROR.stroke;
      case EVWriteBackStatus.IN_PROGRESS:
      case EVWriteBackStatus.QUEUED:
        return STATUS_COLORS.IN_PROGRESS.stroke;
      case EVWriteBackStatus.MANUAL_REQUIRED:
        return STATUS_COLORS.MANUAL_REQUIRED.stroke;
      default:
        return STATUS_COLORS.DEFAULT.stroke;
    }
  };

  const getBackgroundColorBasedOnStatus = () => {
    switch (value.status) {
      case EVWriteBackStatus.SUCCESS:
        return STATUS_COLORS.SUCCESS.background;
      case EVWriteBackStatus.ERROR:
        return STATUS_COLORS.ERROR.background;
      case EVWriteBackStatus.IN_PROGRESS:
      case EVWriteBackStatus.QUEUED:
        return STATUS_COLORS.IN_PROGRESS.background;
      case EVWriteBackStatus.MANUAL_REQUIRED:
        return STATUS_COLORS.MANUAL_REQUIRED.background;
      default:
        return STATUS_COLORS.DEFAULT.background;
    }
  };

  const shouldPropagateEventOrOpenModal = () => {
    if (value.shouldPropagate === true) {
      propagateEventOnClickInRpaStatusCell?.(value?.id ?? "");
    } else {
      dispatch(
        setOpenedModalId({
          id: ModalId.IMAGE_VIEWER_MODAL,
          metaData: { filePaths: value.filePaths },
        }),
      );
    }
  };

  const shiftMasterQueueStatusForWhichModalShouldOpen = [
    MedicalPaOrdersAuthStatus.Pending,
    MedicalPaOrdersAuthStatus.AuthRequired,
    MedicalPaOrdersAuthStatus.Query,
    MedicalPaOrdersAuthStatus.Hold,
  ] as string[];

  const getStatusText = () => {
    switch (value.status) {
      case EVWriteBackStatus.SUCCESS:
        return "BO notes are added";
      case EVWriteBackStatus.ERROR:
        return "Failed to add BO notes";
      case EVWriteBackStatus.IN_PROGRESS:
      case EVWriteBackStatus.QUEUED:
        return "BO notes are being added";
      case EVWriteBackStatus.MANUAL_REQUIRED:
        return "Manual action required";
      default:
        return "BO notes not initiated";
    }
  };

  return (
    <div
      className="table-cell-rpa-status ml-auto h-fit w-fit"
      onMouseEnter={() => setIsCommentModalOpen(true)}
      onMouseLeave={() => setIsCommentModalOpen(false)}
      ref={commentModalRef}
      onClick={(e) => {
        e.stopPropagation();
        if (
          shiftMasterQueueStatusForWhichModalShouldOpen.includes(
            value?.masterAuthStatus ?? "",
          ) &&
          value.status === EVWriteBackStatus.NOT_INITIATED
        ) {
          shouldPropagateEventOrOpenModal();
        } else if (value.status === EVWriteBackStatus.SUCCESS) {
          shouldPropagateEventOrOpenModal();
        }
      }}
    >
      <CallBackIconWithBgColor
        fillColor={getBackgroundColorBasedOnStatus()}
        strokeColor={getColorBasedOnStatus()}
        height={"24px"}
        width={"24px"}
      />
      <ToolTipUsingPortal
        children={getStatusText()}
        placement={"left"}
        showTooltip={isCommentModalOpen}
        parentRef={commentModalRef}
      />
    </div>
  );
};

export default RpaStatusCell;
